import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, LogIn, Lock, AlertTriangle, Webhook, KeyRound, RefreshCw, Send, Mail, CheckCircle2, XCircle,
} from "lucide-react";

const CATS = [
  { id: "all", label: "Tout", icon: ShieldAlert },
  { id: "failed_login", label: "Connexions échouées", icon: LogIn },
  { id: "lockout", label: "Verrouillages", icon: Lock },
  { id: "suspicious", label: "Tentatives suspectes", icon: AlertTriangle },
  { id: "webhook", label: "Webhooks Stripe rejetés", icon: Webhook },
  { id: "admin_access", label: "Accès admin sensibles", icon: KeyRound },
];

const SEV = {
  high: "bg-red-100 text-red-700 border-red-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  info: "bg-slate-100 text-slate-600 border-slate-200",
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}

export default function AdminSecurity() {
  const [view, setView] = useState("events"); // events | alerts
  const [cat, setCat] = useState("all");
  const [data, setData] = useState({ events: [], total: 0, counts_7d: {} });
  const [alerts, setAlerts] = useState({ alerts: [], total: 0, recipient: "" });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [weekly, setWeekly] = useState(false);

  const load = (c = cat) => {
    setLoading(true);
    api.get(`/admin/security-events?category=${c}&limit=150`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const loadAlerts = () => {
    setLoading(true);
    api.get(`/admin/security-alerts?limit=150`)
      .then((r) => setAlerts(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const sendTest = () => {
    setTesting(true);
    api.post(`/admin/security-alert-test`)
      .then((r) => {
        const d = r.data;
        if (d.sent) toast.success(`E-mail de test envoyé à ${d.recipient}. Vérifiez votre boîte de réception.`);
        else toast.warning(`Déclenché, mais l'envoi a échoué (${d.reason || "e-mail non configuré"}). Destinataire : ${d.recipient}`);
        if (view === "alerts") loadAlerts();
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setTesting(false));
  };

  const sendWeekly = () => {
    setWeekly(true);
    api.post(`/admin/security-weekly-summary`)
      .then((r) => {
        const d = r.data;
        if (d.sent) toast.success(`Résumé hebdomadaire envoyé à ${d.recipient}.`);
        else toast.warning(`Déclenché, mais l'envoi a échoué (${d.reason || "e-mail non configuré"}).`);
        if (view === "alerts") loadAlerts();
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setWeekly(false));
  };

  useEffect(() => {
    if (view === "events") load(cat); else loadAlerts();
    /* eslint-disable-next-line */
  }, [cat, view]);

  const counts = data.counts_7d || {};

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="admin-security-page">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C6A87C]">Sécurité</p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">Journal de sécurité</h1>
        <div className="flex items-center gap-2">
          <button data-testid="security-test-alert" onClick={sendTest} disabled={testing}
            className="inline-flex items-center gap-2 rounded-full bg-[#C6A87C] px-4 py-2 text-sm font-medium text-[#0F172A] hover:brightness-95 disabled:opacity-60">
            <Send className="h-4 w-4" /> {testing ? "Envoi…" : "Envoyer une alerte de test"}
          </button>
          <button data-testid="security-weekly-summary" onClick={sendWeekly} disabled={weekly}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            <Mail className="h-4 w-4" /> {weekly ? "Envoi…" : "Résumé hebdomadaire"}
          </button>
          <button data-testid="security-refresh" onClick={() => (view === "events" ? load() : loadAlerts())}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
      </div>
      <p className="mt-2 max-w-2xl text-sm text-slate-500">
        Surveillance de sécurité. Aucune donnée confidentielle (mot de passe, code, clé, jeton, coordonnées bancaires) n'est enregistrée, affichée ni envoyée par e-mail.
      </p>

      {/* Bascule Évènements / Alertes envoyées */}
      <div className="mt-6 inline-flex rounded-full border border-slate-200 p-1">
        <button data-testid="security-view-events" onClick={() => setView("events")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "events" ? "bg-[#0F172A] text-white" : "text-slate-600"}`}>Évènements</button>
        <button data-testid="security-view-alerts" onClick={() => setView("alerts")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "alerts" ? "bg-[#0F172A] text-white" : "text-slate-600"}`}>Alertes e-mail envoyées</button>
      </div>

      {view === "events" && (<>
      {/* Résumé 7 jours */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="security-summary">
        {CATS.filter((c) => c.id !== "all").map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /></div>
              <p className="mt-2 text-2xl font-semibold text-[#0F172A]" data-testid={`security-count-${c.id}`}>{counts[c.id] || 0}</p>
              <p className="text-xs text-slate-500">{c.label} (7j)</p>
            </div>
          );
        })}
      </div>

      {/* Filtres */}
      <div className="mt-8 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c.id} data-testid={`security-filter-${c.id}`} onClick={() => setCat(c.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cat === c.id ? "bg-[#0F172A] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Table évènements */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Évènement</th>
              <th className="px-4 py-3">Gravité</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Compte / détail</th>
            </tr>
          </thead>
          <tbody data-testid="security-events-table">
            {data.events.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Aucun évènement dans cette catégorie.</td></tr>
            )}
            {data.events.map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid="security-event-row">
                <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmt(e.at)}</td>
                <td className="px-4 py-3 font-medium text-[#0F172A]">{e.label}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${SEV[e.severity] || SEV.info}`}>{e.severity}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.ip || "—"}</td>
                <td className="px-4 py-3 text-slate-600">
                  {e.email || ""}
                  {e.path ? <span className="ml-1 font-mono text-xs text-slate-400">{e.path}</span> : ""}
                  {e.reason ? <span className="ml-1 text-xs text-slate-400">({e.reason})</span> : ""}
                  {e.retry_after_s ? <span className="ml-1 text-xs text-slate-400">— {Math.round(e.retry_after_s / 60)} min</span> : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-400">{data.total} évènement(s) au total.</p>
      </>)}

      {view === "alerts" && (
        <div className="mt-6">
          <p className="mb-3 flex items-center gap-2 text-sm text-slate-500">
            <Mail className="h-4 w-4" /> Alertes envoyées à <span className="font-medium text-[#0F172A]">{alerts.recipient || "—"}</span>. Les évènements répétés sont regroupés pour éviter le spam.
          </p>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Alerte</th>
                  <th className="px-4 py-3">Criticité</th>
                  <th className="px-4 py-3">Nb</th>
                  <th className="px-4 py-3">IP / appareil</th>
                  <th className="px-4 py-3">Localisation</th>
                  <th className="px-4 py-3">Envoi</th>
                </tr>
              </thead>
              <tbody data-testid="security-alerts-table">
                {alerts.alerts.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Aucune alerte envoyée pour l'instant.</td></tr>
                )}
                {alerts.alerts.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid="security-alert-row">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmt(a.at)}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{a.label}{a.email ? <span className="ml-1 text-xs text-slate-400">— {a.email}</span> : ""}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${a.critical ? SEV.high : SEV.warning}`}>{a.critical ? "critique" : "surveillance"}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.count || 1}</td>
                    <td className="px-4 py-3 text-slate-500"><span className="font-mono text-xs">{a.ip || "—"}</span>{a.device ? <span className="ml-1 text-xs text-slate-400">· {a.device}</span> : ""}</td>
                    <td className="px-4 py-3 text-slate-500" data-testid="security-alert-geo">
                      {[a.geo_city, a.geo_region, a.country].filter(Boolean).join(", ") || "non disponible"}
                      {a.geo_isp ? <span className="block text-xs text-slate-400">{a.geo_isp}{a.geo_network ? ` · ${a.geo_network}` : ""}</span> : ""}
                    </td>
                    <td className="px-4 py-3">
                      {a.sent ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> envoyé</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-red-500"><XCircle className="h-3.5 w-3.5" /> échec</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-400">{alerts.total} alerte(s) au total.</p>
        </div>
      )}
    </div>
  );
}
